import { useLocation } from '@reach/router';
import { differenceInCalendarDays } from 'date-fns';
import { Slice, withPrefix } from 'gatsby';
import Cookies from 'js-cookie';
import isString from 'lodash/isString';
import React, { ReactNode, useEffect } from 'react';

import { useCustomer } from '@/hooks/useCustomer';
import { useIsHemX } from '@/hooks/useIsHemX';

import { IS_DEVELOPMENT } from '../constants';
import { LocaleRedirect } from './LocaleRedirect';
import { Panels } from './Panels';
import { PreviewRefresh } from './PreviewRefresh';

const getUrlParam = (name: string) => {
  const results = new RegExp('[?&]' + name + '=([^&#]*)').exec(
    window.location.search,
  );
  return results !== null ? results[1] || 0 : false;
};

export const Layout: React.FC<{
  children: ReactNode;
}> = ({ children }): JSX.Element => {
  const location = useLocation();

  const { isHemX } = useIsHemX();
  const { customerAccessToken, renewCustomerAccessToken, logout } =
    useCustomer();

  const isHomePage = location.pathname === withPrefix('/');

  useEffect(() => {
    const utmTags = [
      getUrlParam('utm_source'),
      getUrlParam('utm_medium'),
      getUrlParam('utm_term'),
    ]
      .filter(Boolean)
      .join('_');

    if (!!utmTags && utmTags !== '') {
      Cookies.set('utmTags', utmTags);
    }

    const klaviyoProfileId = getUrlParam('utm_profileid');
    if (!!klaviyoProfileId && klaviyoProfileId !== '') {
      Cookies.set('hem-klaviyo-profile-id', klaviyoProfileId);
    }
  }, []);

  useEffect(() => {
    if (isString(customerAccessToken?.accessToken)) {
      const now = new Date();
      const accessTokenExpiresAt = new Date(customerAccessToken?.expiresAt);
      if (accessTokenExpiresAt < now) {
        // token has expired
        logout();
      } else {
        // token expires within 10 days
        if (differenceInCalendarDays(accessTokenExpiresAt, now) <= 10) {
          renewCustomerAccessToken();
        }
      }
    }

    // if url contains query param checkout_url, call logout()
    if (getUrlParam('checkout_url')) {
      logout();
    }
  }, [
    customerAccessToken,
    customerAccessToken?.accessToken,
    customerAccessToken?.expiresAt,
    logout,
    renewCustomerAccessToken,
  ]);

  return (
    <>
      <LocaleRedirect />

      <a href="#maincontent" className="sr-only">
        Skip to main content
      </a>

      <Slice
        alias="navbar"
        isHomePage={isHomePage}
        isHemX={isHemX}
        // @ts-expect-error location is serializable (OK!) to pass to slices
        location={location}
      />

      <main id="maincontent">{children}</main>

      <Slice alias="footer" isHomePage={isHomePage} />

      <Panels />

      <Slice alias="newsletter-popup" pathname={location.pathname} />

      {IS_DEVELOPMENT ? <PreviewRefresh /> : null}
    </>
  );
};
